// utils/mixpanel.js

import mixpanel from "mixpanel-browser";

import { isStaging } from "@/utils/environment";
let lastTrackedPage = "";

if (isStaging) {
  mixpanel.init(process.env.mixpanelStaging as string, {
    debug: true,
    track_pageview: true,
    persistence: "localStorage",
  });
} else {
  mixpanel.init(process.env.mixpanelProduction as string, {
    track_pageview: true,
    persistence: "localStorage",
  });
}

export const trackPageView = (page: string) => {
  if (page === lastTrackedPage) return;

  mixpanel.track("Page View", { page });
  lastTrackedPage = page;
};

/**
 * Identify the user in Mixpanel.
 * @param {string} userId - The unique identifier for the user.
 */
export const identifyUser = (userId: string) => {
  if (userId) {
    mixpanel.identify(userId);
  } else {
    console.warn("Mixpanel: User ID is undefined");
  }
};

/**
 * Track the First Login Date (set once).
 * @param {string} userId - The unique identifier for the user.
 */
export const trackFirstLoginDate = (userId: string) => {
  identifyUser(userId);
  mixpanel.people.set_once({
    First_Login_Date: new Date().toISOString(),
  });
};

/**
 * Set the Verified status to true.
 * @param {string} userId - The unique identifier for the user.
 */
export const setVerified = (userId: string, status: boolean) => {
  identifyUser(userId);
  mixpanel.people.set({
    Verified: status,
  });
};

/**
 * Set the Registered Date (set once).
 * @param {string} userId - The unique identifier for the user.
 */
export const setRegisteredDate = (userId: string, data: string) => {
  identifyUser(userId);
  mixpanel.people.set_once({
    Registered_Date: new Date(data).toISOString(),
  });
};

/**
 * Set the Onboarding Completed Date (set once).
 * @param {string} userId - The unique identifier for the user.
 */
export const setOnboardingCompletedDate = (userId: string) => {
  identifyUser(userId);
  mixpanel.people.set_once({
    Onboarding_Completed_Date: new Date().toISOString(),
  });
};

/**
 * Set the Published Date (set once).
 * @param {string} userId - The unique identifier for the user.
 */
export const setPublishedDate = (userId: string) => {
  identifyUser(userId);
  mixpanel.people.set_once({
    Published_Date: new Date().toISOString(),
  });
};

/**
 * Generic function to track click events.
 * @param {string} eventName - The name of the event.
 */
export const trackClickEvent = (eventName: string) => {
  mixpanel.track(eventName);
};

export const setUserType = (userType: "talent" | "recruiter" | string) => {
  mixpanel.people.set({
    "User Type": userType,
  });
};

/**
 * Track login method.
 * @param {string} method - 'Google', 'GitHub', or 'Email'.
 */
export const trackLoginMethod = (method: "Google" | "GitHub" | "Email") => {
  mixpanel.track("Login", {
    method,
  });
};

/**
 * Track when Terms and Conditions are viewed.
 */
export const trackViewTermsAndConditions = () => {
  mixpanel.track("View Terms and Conditions link in the login page");
};

/**
 * Track when Privacy Policy is viewed.
 */
export const trackViewPrivacyPolicy = () => {
  mixpanel.track("View Privacy Policy link in the login page");
};

/**
 * Track whether the user chooses AI or manual entry.
 * @param {string} choice - 'AI' or 'Manual'.
 */
export const trackUserEntryChoice = (choice: "AI" | "Manual") => {
  mixpanel.track("User Entry Choice for onboarding", {
    choice,
  });
};

/**
 * Track AI flow events.
 * @param {string} action - 'Upload Resume' or 'Copy/Paste Resume'.
 */
export const trackAIFlowAction = (
  action: "Upload Resume" | "Copy/Paste Resume"
) => {
  mixpanel.track("AI Flow Action", {
    action,
  });
};

/**
 * Track manual flow step completion.
 * @param {number} stepNumber - The step number that was completed.
 */
export const trackManualFlowStepCompleted = (stepNumber: number) => {
  mixpanel.track("Manual Flow Step Completed", {
    step: stepNumber,
  });
};

/**
 * Specific functions for each click event.
 */
export const trackClickStartBuildingCareerStory = () => {
  trackClickEvent("Click on Start Building Your Career Story Now");
};

export const trackClickSignIn = () => {
  trackClickEvent("Click on Sign In");
};

export const trackClickSignUp = () => {
  trackClickEvent("Click on Sign Up");
};

export const trackClickGetEarlyInvitation = () => {
  trackClickEvent("Click on Get An Early Invitation");
};

export const trackClickGetStarted = () => {
  trackClickEvent("Click on Get Started");
};

export const trackClickAboutUs = () => {
  trackClickEvent("Click on About Us");
};

export const trackClickPrivacyPolicy = () => {
  trackClickEvent("Click on Privacy Policy");
};

export const trackClickTermsOfService = () => {
  trackClickEvent("Click on Terms of Service");
};

export const trackClickFacebookIcon = () => {
  trackClickEvent("Click on Facebook icon");
};

export const trackClickInstagramIcon = () => {
  trackClickEvent("Click on Instagram icon");
};

export default mixpanel;
